import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CONSTANTS } from "@constants/index";
import { vatHandlingType } from "@enums/hooks.enum";
import { PriceBlockProps } from "@models/components/user.model";
import { formatPrice } from "@utils/common.utils";

import Price from "../Price/Price";

const PriceBlock: React.FC<PriceBlockProps> = ({ bookingDetails }) => {
  const { t } = useTranslation();
  const vatPercentage =
    bookingDetails?.vatPercentage || bookingDetails?.vatHandlingType
      ? bookingDetails?.vatPercentage
      : null;

  const vatBreakdown = useMemo(() => {
    const breakdownMap: Record<string, number> = {};

    // 1. Add resource VAT
    if (bookingDetails?.vat && bookingDetails?.vatPercentage) {
      const rateStr = bookingDetails.vatPercentage
        .toString()
        .replace("%", "")
        .trim();
      const rateVal = parseFloat(rateStr);
      if (!isNaN(rateVal) && bookingDetails.vat > 0) {
        const key = rateVal.toString();
        breakdownMap[key] = (breakdownMap[key] || 0) + bookingDetails.vat;
      }
    }

    // 2. Add amenities VAT
    if (bookingDetails?.amenities) {
      bookingDetails.amenities.forEach(amenity => {
        const vatAmount = parseFloat(amenity.vat?.toString() || "0");
        if (vatAmount > 0 && amenity.vatPercentage) {
          const rateStr = amenity.vatPercentage
            .toString()
            .replace("%", "")
            .trim();
          const rateVal = parseFloat(rateStr);
          if (!isNaN(rateVal)) {
            const key = rateVal.toString();
            breakdownMap[key] = (breakdownMap[key] || 0) + vatAmount;
          }
        }
      });
    }

    return Object.entries(breakdownMap)
      .map(([rateStr, amount]) => ({
        amount,
        rate: parseFloat(rateStr),
      }))
      .filter(item => item.amount > 0)
      .sort((a, b) => b.rate - a.rate);
  }, [
    bookingDetails?.vat,
    bookingDetails?.vatPercentage,
    bookingDetails?.amenities,
  ]);

  const priceLabelKey = useMemo(() => {
    if (bookingDetails?.vat) {
      if (bookingDetails.vatHandlingType === vatHandlingType.INCLUSIVE) {
        return "priceOfTheUnitIncl";
      }
      if (bookingDetails.vatHandlingType === vatHandlingType.EXCLUSIVE) {
        return "priceOfTheUnitExcl";
      }
    }
    return "priceOfTheUnit";
  }, [bookingDetails?.vat, bookingDetails?.vatHandlingType]);

  const priceWithVat = useMemo(() => {
    if (bookingDetails?.vat) {
      if (bookingDetails.vatHandlingType === vatHandlingType.INCLUSIVE) {
        return formatPrice(
          bookingDetails?.totalPrice,
          bookingDetails?.currency || ""
        );
      } else if (bookingDetails.vatHandlingType === vatHandlingType.EXCLUSIVE) {
        return formatPrice(
          bookingDetails?.totalPrice - (bookingDetails?.totalVat ?? 0),
          bookingDetails?.currency || ""
        );
      } else {
        return formatPrice(
          bookingDetails?.totalPrice,
          bookingDetails?.currency || ""
        );
      }
    }
    return formatPrice(
      bookingDetails?.totalPrice,
      bookingDetails?.currency || ""
    );
  }, [bookingDetails?.vat, bookingDetails?.vatHandlingType]);

  return (
    <div className="total-prize-section mb-2" data-testid="price-block">
      <Price
        label={t(priceLabelKey)}
        recursiveBooking={bookingDetails?.recursiveBooking}
        labelClass="unit-prize-label"
        value={priceWithVat}
      />
      {(bookingDetails?.totalVat ?? 0) > 0 && vatPercentage && (
        <Price
          label={t("totalVat")}
          labelClass="unit-vat-label"
          subLabel={null}
          value={formatPrice(
            bookingDetails?.totalVat ?? 0,
            bookingDetails?.currency || ""
          )}
        />
      )}
      {(bookingDetails?.totalVat ?? 0) > 0 &&
        vatBreakdown.length > 0 &&
        vatBreakdown.map((item, index) => (
          <div
            key={index}
            className="row unit-vat-breakdown-row align-items-center mt-1 mb-1"
          >
            <div className="col-7">
              <div
                className="fg-FS-14 fg-text-primary font-weight-light d-inline-flex align-items-center gap-2"
                data-testid="price-label"
              >
                <span>{t("VAT")}</span>{" "}
                <span
                  className={`badge rounded-pill fg-FS-10 px-2 py-1 fg-vat-badge ${
                    item.rate > CONSTANTS.VAT_STANDARD_RATE
                      ? "fg-vat-high"
                      : "fg-vat-standard"
                  }`}
                >
                  {item.rate}%
                </span>
              </div>
            </div>
            <div className="col-5 text-end">
              <div
                className="fg-FS-14 fg-text-primary font-weight-light"
                data-testid="price-text"
              >
                {formatPrice(item.amount, bookingDetails?.currency || "")}
              </div>
            </div>
          </div>
        ))}
      <Price
        customClass="mt-2"
        label={
          bookingDetails?.recursiveBooking
            ? t("totalRecursivePriceToPay")
            : t("totalPriceToPay")
        }
        recursiveBooking={bookingDetails?.recursiveBooking}
        value={formatPrice(
          bookingDetails?.totalPrice ?? 0,
          bookingDetails?.currency || ""
        )}
      />
    </div>
  );
};

export default PriceBlock;
